import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import PublicLayout from '../components/PublicLayout';
import { LavashLogo } from '../components/brand/Logos';
import { NoYeastIcon, NoPreservativesIcon, CleanIcon, LeafIcon } from '../components/brand/Icons';
import { ArrowRight, Lock, Tag, Truck, Receipt } from 'lucide-react';

export default function LavashPage() {
  const { t } = useLanguage();
  const advs = [
    { Icon: NoYeastIcon, label: t('advNoYeast') },
    { Icon: NoPreservativesIcon, label: t('advNoPreservatives') },
    { Icon: CleanIcon, label: t('advClean') },
    { Icon: LeafIcon, label: t('advVersatile') },
  ];
  const sizes = ['25 cm', '30 cm', '36 cm', '40 cm', 'Mini', 'Wrap'];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-[#F2EFE6]" data-testid="lavash-page">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-[#556B2F] eyebrow mb-6">LAVASH</p>
              <LavashLogo size="lg" className="mb-8" />
              <h1 className="h-display text-4xl md:text-6xl text-[#556B2F] mb-6">{t('tagLavashSlogan')}</h1>
              <p className="text-base md:text-lg text-[#556B2F]/75 max-w-xl mb-10 leading-relaxed">{t('lavashCardDesc')}</p>
              <Link to="/register" className="cff-button-lavash inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm" data-testid="lavash-order-cta">
                {t('ctaLoginToOrder')} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-square bg-[#556B2F] rounded-2xl relative overflow-hidden flex items-center justify-center bg-grain">
                <div className="text-[#A4B18E]/20 text-[200px] font-extrabold tracking-tighter select-none">L</div>
                <div className="absolute inset-8 border border-[#A4B18E]/30 rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="bg-[#F6F4EE] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="eyebrow mb-6">{t('lavashAdv')}</p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-16 max-w-2xl">
            {t('betterBreadBetterLife')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {advs.map(({Icon, label}, i) => (
              <div key={i} className="bg-[#F2EFE6] p-8 rounded-xl border border-[#A4B18E]/30 hover:border-[#556B2F] transition-colors">
                <div className="text-[#556B2F] mb-4 inline-flex p-2.5 rounded-full bg-[#A4B18E]/20"><Icon width={26} height={26} /></div>
                <p className="text-[10px] tracking-[0.3em] text-[#556B2F] mb-2 font-semibold">0{i+1}</p>
                <p className="text-sm font-bold text-[#556B2F]">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Catalog preview */}
      <section className="bg-[#F2EFE6] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-[#556B2F] eyebrow mb-6">{t('catalogPreview')}</p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-16 text-[#556B2F] max-w-2xl">
            {t('sixSizes')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {sizes.map((s, i) => (
              <div key={s} className="aspect-square bg-[#F6F4EE] rounded-xl flex flex-col items-center justify-center border border-[#A4B18E]/30 hover:border-[#556B2F] transition-colors cff-hover-lift">
                <p className="text-[10px] tracking-widest text-[#556B2F]/50 mb-2">LAVASH</p>
                <p className="text-2xl font-extrabold text-[#556B2F]">{s}</p>
                <p className="text-[10px] tracking-widest text-[#556B2F]/40 mt-2 uppercase">Modern</p>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <Link to="/register" className="cff-button-lavash inline-flex items-center gap-2 px-8 py-4 rounded-md text-base" data-testid="lavash-bottom-cta">
              {t('ctaLoginToOrder')} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* B2B Pricing Notice — visible to Toss reviewers and guests */}
      <section className="bg-[#1A1A1A] text-[#F6F4EE] py-20 md:py-28" data-testid="b2b-notice-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A352]/15 text-[#C9A352] rounded-sm mb-6">
                <Lock className="h-3 w-3" />
                <span className="text-[10px] tracking-[0.25em] uppercase font-bold">{t('b2bNoticeTag')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight mb-6">
                {t('b2bNoticeTitle')}
              </h2>
              <p className="text-base text-[#F6F4EE]/65 leading-relaxed mb-8">
                {t('b2bNoticeBody')}
              </p>
              <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-[#C9A352] hover:bg-[#B8923E] text-[#1A1A1A] text-sm font-bold transition-colors" data-testid="b2b-register-cta">
                {t('register')} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {[
                { Icon: Tag, label: t('b2bAdvWholesale') },
                { Icon: Receipt, label: t('b2bAdvVolume') },
                { Icon: Truck, label: t('b2bAdvDelivery') },
                { Icon: Receipt, label: t('b2bAdvInvoice') },
              ].map((adv, i) => (
                <div key={i} className="bg-[#F6F4EE]/5 border border-[#F6F4EE]/10 rounded-lg p-4">
                  <div className="text-[#C9A352] mb-2"><adv.Icon className="h-4 w-4" /></div>
                  <p className="text-sm font-semibold text-[#F6F4EE]">{adv.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
